// composables/useSupplierDealReport.js
//
// Data layer for the supplier "final report" (post-mortem) screen — a per-deal,
// per-supplier performance snapshot for ONE closed Poola deal. Read-only, static
// (the deal is closed → no realtime, no polling; a single fetch on resolve).
// Requirements reference:
//   _bmad-output/planning-artifacts/poola-supplier-deal-report-brainstorm-2026-07-15.md
// Delivered BE contract:
//   ../backend/_bmad-output/sync/briefs/2026-07-15-poola-supplier-deal-report-aggregate-BE-REPLY.md
//
// It composes TWO sources into one view-model:
//   1. the BE aggregate (people + poolcredit + the named leaderboard) — the
//      counts that are NOT derivable client-side;
//   2. the viewer's own (`isMine`) offers — the money + sell-through half,
//      derived client-side (Σ per-unit cents × piecesClaimed).
//
// ── v1 deal resolution (accepted limitation) ────────────────────────────────
// There is NO deal picker (BE deferred the participated-by list — see the reply
// "Secondary ask … SKIPPED"). v1 targets the most-recent ENDED deal, read off
// usePoolaDeal's singleton /poola-deals/current: when that run is in
// POOLA_STATE.ENDED the report resolves against its id. When /current is
// SCHEDULED / ACTIVE / EMPTY (no closed run currently surfaced) there is no deal
// to report → `notAvailable`. This is the same window the finale card uses.
//
// MONEY: every *Cents value in the view-model is integer EGP cents, exactly as it
// arrives from the wire — NEVER pre-formatted and NEVER rounded here. Fractions
// (sell-through, penetration, discount depth) are kept full-precision; round only
// at format time in the UI (useCurrency().formatCurrency for cents; Math.round(
// fraction * 100) for percentages).
import { computed, ref, unref, watch } from 'vue';
import { usePoolaDeal, POOLA_STATE } from '@/composables/usePoolaDeal.js';
import { POOLA_OFFER_STATUS } from '@/constants/poola.js';
import {
  fetchSupplierReport,
  fetchMyOffersForDeal,
  POOLA_REPORT_FORBIDDEN,
  POOLA_REPORT_NOT_AVAILABLE,
} from '@/services/poolaReport.service.js';

/** Read a value / ref / getter uniformly. */
function read(value) {
  return typeof value === 'function' ? value() : unref(value);
}

/** Stable string compare for JSON:API ids (all ids are strings on the wire, but
 *  a pinia-orm uid or a numeric fixture could sneak a number in — coerce both). */
function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

/** An offer completed ⇔ it sold out. The scoped read returns PLAIN objects (not
 *  PoolaOffer model instances), so the model's `isComplete` getter isn't
 *  available — read the authoritative closed-deal `status` instead. */
export function isOfferCompleted(offer) {
  return Number(offer?.status) === POOLA_OFFER_STATUS.SOLD_OUT;
}

// ── Pure composition helpers (exported for unit tests) ───────────────────────
// Each takes plain data (the aggregate object + the `isMine` offers array) and
// returns a plain view-model slice. No reactivity, no I/O — deterministic and
// integer-cent-safe (Σ of int×int stays int; only ratios are fractional).

/**
 * Money moved, client-derived from the viewer's `isMine` offers (Section C).
 * `piecesClaimed` is net-of-refund, so these reflect CURRENTLY-HELD claims.
 */
export function composeMoney(myOffers = []) {
  let grossCents = 0;
  let cashCents = 0;
  let poolshareCents = 0;
  for (const o of myOffers) {
    const claimed = Number(o?.piecesClaimed) || 0;
    grossCents += (Number(o?.unitPriceCents) || 0) * claimed;
    cashCents += (Number(o?.cashCents) || 0) * claimed;
    poolshareCents += (Number(o?.poolshareCents) || 0) * claimed;
  }
  return {
    grossCents,
    cashCents,
    poolshareCents,
    // بولشير as a fraction of gross (0..1) — the brainstorm's "avgDiscountPct".
    // Kept as a full-precision fraction; the UI renders Math.round(x * 100) + '%'.
    avgDiscountFraction: grossCents > 0 ? poolshareCents / grossCents : 0,
  };
}

/**
 * Reach (Section B): distinct buyers vs the deal-wide distinct-member
 * denominator (penetration), plus units sold vs offered (sell-through, derived
 * from the `isMine` offers).
 */
export function composeReach(report, myOffers = []) {
  const distinctBuyerCount = Number(report?.viewer?.distinctBuyerCount) || 0;
  const distinctMemberCount = Number(report?.distinctMemberCount) || 0;
  let unitsSold = 0;
  let unitsOffered = 0;
  for (const o of myOffers) {
    unitsSold += Number(o?.piecesClaimed) || 0;
    unitsOffered += Number(o?.piecesTotal) || 0;
  }
  return {
    distinctBuyerCount,
    distinctMemberCount,
    // Guarded so it always holds distinctMemberCount ≥ distinctBuyerCount (BE
    // invariant), but clamp defensively so a malformed denominator can't exceed 1.
    penetrationFraction:
      distinctMemberCount > 0 ? Math.min(1, distinctBuyerCount / distinctMemberCount) : 0,
    unitsSold,
    unitsOffered,
    sellThroughFraction: unitsOffered > 0 ? unitsSold / unitsOffered : 0,
  };
}

/**
 * Hero (Section A): the win. poolcredit generated + distinct earners (one wallet
 * relationship — see the brainstorm glossary) + completed / total offers.
 */
export function composeHero(report, myOffers = []) {
  const total = myOffers.length;
  const completed = myOffers.filter(isOfferCompleted).length;
  return {
    poolcreditReturnedCents: Number(report?.viewer?.poolcreditReturnedCents) || 0,
    distinctEarnerCount: Number(report?.viewer?.distinctEarnerCount) || 0,
    members: Number(report?.viewer?.distinctEarnerCount) || 0, // alias — same figure, hero framing
    completed,
    total,
    completionFraction: total > 0 ? completed / total : 0,
  };
}

/**
 * Per-offer detail rows (Section D). One row per `isMine` offer, LEFT-joined to
 * the aggregate's per-offer counts by `offerId`. An aggregate row with no
 * matching loaded offer is ignored (rows are driven by the offers we hold); an
 * offer with no matching aggregate row gets null counts. Not-completed offers
 * show poolcredit as null (UI renders «—») even if the aggregate carries a value.
 */
export function composePerOffer(myOffers = [], perOfferAgg = []) {
  const byOfferId = new Map();
  for (const row of perOfferAgg) {
    if (row?.offerId != null) byOfferId.set(String(row.offerId), row);
  }
  return myOffers.map((o) => {
    const claimed = Number(o?.piecesClaimed) || 0;
    const total = Number(o?.piecesTotal) || 0;
    const completed = isOfferCompleted(o);
    const agg = byOfferId.get(String(o?.id)) ?? null;
    return {
      id: o?.id ?? null,
      displayTitle: o?.displayTitle ?? null,
      photoUrl: o?.photoUrl ?? null,
      completed,
      piecesTotal: total,
      piecesClaimed: claimed,
      sellThroughFraction: total > 0 ? claimed / total : 0,
      cashCents: (Number(o?.cashCents) || 0) * claimed,
      poolshareCents: (Number(o?.poolshareCents) || 0) * claimed,
      grossCents: (Number(o?.unitPriceCents) || 0) * claimed,
      // From the aggregate join (null when unmatched → UI shows «—»).
      distinctBuyerCount: agg ? Number(agg.distinctBuyerCount) || 0 : null,
      // Only completed offers funded poolcredit; not-completed → null («—»).
      poolcreditReturnedCents: completed && agg ? Number(agg.poolcreditReturnedCents) || 0 : null,
    };
  });
}

/**
 * Leaderboard (Section E). The aggregate ships it already ranked (poolcredit
 * desc, then units desc, then id) — pass it through with a 1-based `rank` and a
 * viewer flag, and surface the viewer's own rank + the field size.
 */
export function composeLeaderboard(leaderboard = [], viewerSupplierId = null) {
  const rows = leaderboard.map((row, index) => ({
    ...row,
    rank: index + 1,
    isViewer: sameId(row?.supplierId, viewerSupplierId),
  }));
  const mine = rows.find((r) => r.isViewer) ?? null;
  return {
    rows,
    myRank: mine ? mine.rank : null, // null when the viewer isn't in the board
    totalSuppliers: rows.length,
  };
}

/**
 * Compose the full report view-model from the two sources. Pure — the composable
 * calls this after both fetches resolve; tests exercise it directly.
 *
 * @param {object} report — `meta.supplierReport` from fetchSupplierReport.
 * @param {object[]} myOffers — the viewer's `isMine` offers for the deal.
 */
export function composeViewModel(report, myOffers = []) {
  const viewerSupplierId = report?.viewer?.supplierId ?? null;
  const leaderboard = composeLeaderboard(report?.leaderboard ?? [], viewerSupplierId);
  return {
    dealId: report?.dealId ?? null,
    viewerSupplierId,
    hero: composeHero(report, myOffers),
    reach: composeReach(report, myOffers),
    money: composeMoney(myOffers),
    perOffer: composePerOffer(myOffers, report?.viewer?.perOffer ?? []),
    leaderboard: leaderboard.rows,
    myRank: leaderboard.myRank,
    totalSuppliers: leaderboard.totalSuppliers,
  };
}

/**
 * useSupplierDealReport — resolve the target (ended) deal, fetch + compose the
 * report. MUST be called from a component setup() when `autoResolve` is on (the
 * default), because it defaults to spinning up usePoolaDeal (which registers
 * lifecycle hooks).
 *
 * @param {object} [options]
 * @param {{ deal: import('vue').Ref, state: import('vue').Ref }} [options.dealSource]
 *   Inject an existing usePoolaDeal result (or a stub) instead of creating one —
 *   used by tests and by a host that already holds the deal.
 * @param {boolean} [options.autoResolve=true] — when true, watch the deal source
 *   and auto-load once it resolves to an ENDED run. Set false to drive `load()`
 *   manually (tests, or a host that fetches on a user action).
 */
export function useSupplierDealReport(options = {}) {
  const { dealSource, autoResolve = true } = options;
  const poolaDeal = dealSource ?? usePoolaDeal();
  const { deal } = poolaDeal;
  const dealState = poolaDeal.state;

  // ── Resolved target deal (only meaningful in the ENDED window) ──────────────
  const endedDeal = computed(() =>
    read(dealState) === POOLA_STATE.ENDED ? (read(deal) ?? null) : null,
  );
  const dealId = computed(() => endedDeal.value?.id ?? null);
  const dealTitle = computed(() => endedDeal.value?.title ?? null);
  const dateRange = computed(() => ({
    startAt: endedDeal.value?.startAt ?? null,
    endAt: endedDeal.value?.endAt ?? null,
  }));

  // ── Snapshot state (read-only; no store) ────────────────────────────────────
  const report = ref(null); // raw meta.supplierReport
  const myOffers = ref([]); // the viewer's isMine offers for the deal
  const viewModel = ref(null); // composed VM (null until a successful load)

  const loading = ref(false);
  const error = ref(null); // a generic (non-403/404) failure
  const forbidden = ref(false); // 403 → viewer is not a supplier
  const notAvailable = ref(false); // 404, or no ENDED deal currently resolvable
  const hasNoOffers = ref(false); // report exists but viewer had 0 offers in the deal

  // A monotonic token so a superseded load (deal re-resolves mid-flight) can't
  // clobber the latest result in its async tail.
  let loadToken = 0;

  function resetFlags() {
    error.value = null;
    forbidden.value = false;
    notAvailable.value = false;
    hasNoOffers.value = false;
  }

  /**
   * Fetch + compose the report for a specific ended deal id. Directly callable
   * (tests / manual hosts); the auto-resolve watcher calls it with dealId.value.
   */
  async function load(id) {
    // Bump the supersession token FIRST — before the early null-branch — so EVERY
    // load() (even the synchronous no-deal one) invalidates any load still in
    // flight. Without this, an ENDED→null re-resolve mid-flight would leave the
    // in-flight report load's token still current, and its async tail would clobber
    // the null-branch's `notAvailable` with a stale view-model (H1).
    const token = (loadToken += 1);

    const targetId = id ?? dealId.value;
    if (targetId == null) {
      // No closed deal resolvable (see the v1-resolution note above).
      resetFlags();
      notAvailable.value = true;
      loading.value = false;
      report.value = null;
      myOffers.value = [];
      viewModel.value = null;
      return;
    }

    loading.value = true;
    resetFlags();

    // Fire both in parallel; the aggregate is the GATE (403/404 short-circuits),
    // the offers read is the supporting half. Pre-attach a catch so an offers
    // rejection while the report is still pending can't become an unhandled
    // rejection.
    const offersPromise = fetchMyOffersForDeal(targetId).then(
      (v) => ({ ok: true, value: v }),
      (e) => ({ ok: false, error: e }),
    );

    let reportData;
    try {
      reportData = await fetchSupplierReport(targetId);
    } catch (err) {
      await offersPromise; // drain so it never rejects unobserved
      if (token !== loadToken) return; // superseded
      if (err?.code === POOLA_REPORT_FORBIDDEN) forbidden.value = true;
      else if (err?.code === POOLA_REPORT_NOT_AVAILABLE) notAvailable.value = true;
      else error.value = err;
      loading.value = false;
      report.value = null;
      myOffers.value = [];
      viewModel.value = null;
      return;
    }

    const offersResult = await offersPromise;
    if (token !== loadToken) return; // a newer load supersedes this one

    if (!offersResult.ok) {
      // Offers failed but the aggregate succeeded. The money/units section can't
      // be trusted (it would read as silent zeros), so surface it as an error
      // rather than show a half-report.
      error.value = offersResult.error;
      loading.value = false;
      report.value = null;
      myOffers.value = [];
      viewModel.value = null;
      return;
    }

    if (reportData == null) {
      // HTTP 200 with no `meta.supplierReport`. The contract says this can't
      // happen for a supplier on an ENDED deal (they get a report, else 403/404),
      // so treat an anomalous null as "no report" rather than fabricating an
      // all-zero view-model from composeViewModel(null, …) (L1).
      notAvailable.value = true;
      loading.value = false;
      report.value = null;
      myOffers.value = [];
      viewModel.value = null;
      return;
    }

    report.value = reportData;
    myOffers.value = offersResult.value ?? [];
    // "report exists but the viewer had no offers in the deal" — the aggregate's
    // own per-offer list is the AUTHORITATIVE offer-existence signal (empty ⇒
    // none), independent of the client offers fetch.
    //
    // TWO-SOURCE SEAM (M1): hasNoOffers + the hero/reach/leaderboard counts come
    // from the aggregate; money, unitsSold/Offered, and the per-offer rows come
    // from the client `isMine` offers fetch. These describe the same offer set
    // EXCEPT that the /poola-offers index excludes DISABLED offers while the
    // aggregate's perOffer may include them. So a supplier whose offers were all
    // DISABLED at close could read hasNoOffers=false with an all-zero money half
    // and empty per-offer rows. DISABLED-at-close offers had no sales, so the
    // money zeros are truthful, but the per-offer table won't reconcile to the
    // hero total (see the UI handoff note). This is a rare edge; flagged for the
    // owner rather than hard-errored, since the aggregate half stays valid.
    hasNoOffers.value = (reportData.viewer?.perOffer?.length ?? 0) === 0;
    viewModel.value = composeViewModel(reportData, myOffers.value);
    loading.value = false;
  }

  function refresh() {
    return load(dealId.value);
  }

  if (autoResolve) {
    // Drive off both the resolved id and the deal state: once /current settles
    // into an ENDED run we load; a non-ENDED settle (or no run) flips
    // notAvailable via load(null). Skip the transient LOADING phase so we don't
    // prematurely declare notAvailable before the deal fetch resolves.
    watch(
      () => [read(dealState), dealId.value],
      ([currentState]) => {
        if (currentState === POOLA_STATE.LOADING) return; // wait for /current
        load(dealId.value);
      },
      { immediate: true },
    );
  }

  return {
    // Target deal context
    dealId,
    dealTitle,
    dateRange,
    // Snapshot data
    report,
    myOffers,
    viewModel,
    // State flags
    loading,
    error,
    forbidden,
    notAvailable,
    hasNoOffers,
    // Actions
    load,
    refresh,
  };
}
